import {FilterColumnsSchema, FilterGroupsSchema} from "./types";
import {z} from "zod";

export type AnalyticsCfgFilterComparition = "><" | "<=" | ">=" | "==";
export type FilterRelatedPeriodComparition = "inc,>=" | "dec,<=" | "inc,<=" | "dec,>=";

const AnalyticsCfgFilterComparitionSchema = z.enum(["><", "<=", ">=", "=="]);
const FilterRelatedPeriodComparitionSchema = z.enum(["inc,>=", "dec,<=", "inc,<=", "dec,>="]);

// for test
export const AnalyticsCfgFldTypeSchema = z.object({
    name_db: z.string(),
    name_orig: z.string().optional(),
    label: z.string(),
    fk: z.union( [z.literal(0),z.literal(1)] ),
    filter_str: z.string().optional(),
    subtype: z.object({
        'area': z.object({
            'label': z.string(),
            'filter_str': z.string()
        }),
        'radius': z.object({
            'label': z.string(),
            'filter_str': z.string()
        })
    }).optional(),
    desc: z.string().optional(),
    info: z.string().optional(),
    type_gm: z.string(), // z.union([z.literal('money'), z.literal('month'), z.literal('int'), z.literal('ext_fk_id'), z.literal('ext_id') ]),
    filter: z.union([z.literal(0),z.literal(1), z.enum(["0", "1"])]),
    color: z.union( [z.literal(0),z.literal(1), z.string().length(7), z.string().length(4) ] ).optional(),
    colorable: z.union([z.literal(0),z.literal(1)]).default(1),
    attr_gm: z.array(z.string()).optional(),
    cluster: z.string().optional(),
    cluster_cfg: z.record(z.union([z.string(),z.boolean()])).optional(),
    dict: z.record(z.string()).optional(),
    min: z.union([z.coerce.number(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).optional(),
    max: z.union([z.coerce.number(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).optional(),
    unit: z.string().catch(''),
    period_range: z.object({min: z.string(), max: z.string()}).optional(),
    collection: z.string().optional(),
    fld_period: z.string().optional()
});

export interface AnalyticsCfgFldType extends z.infer<typeof AnalyticsCfgFldTypeSchema> { }

const AnalyticsCfgClusterSubMenuParamSchema = z.object({
    label: z.string().catch('Default param label').describe('ex. `do magazynu`)'),
    type: z.union([z.literal('select'), z.literal('checkbox')]),
    order: z.array(z.union([z.literal(0),z.literal(1),z.literal(-1)])).length(2)
        .catch([0,0]).describe('Ask PB how to use order'),
    values: z.array(z.union([z.string(), z.number(), z.boolean()])).nonempty(),
})
export type AnalyticsCfgClusterSubMenuParam = z.infer<typeof AnalyticsCfgClusterSubMenuParamSchema>;

export const AnalyticsCfgClusterSubMenuSchema = z.object({
    label: z.string().catch('Default cluster label').describe('ex. `Dojazd`'),
    info: z.string().catch('').describe('will be shown as help when hovering over the icon'),
    params: z.record(AnalyticsCfgClusterSubMenuParamSchema).describe('https://er.automapa.pl/issues/35518?journals=all#note-229966')
});
export type AnalyticsCfgClusterSubMenu = z.infer<typeof AnalyticsCfgClusterSubMenuSchema>;

export const FilterSubMenuValueSchema = z.object({
    label: z.string(),
    info: z.string().catch(''),
    cfg: z.record(AnalyticsCfgClusterSubMenuParamSchema).optional(),
    items: z.array(
        z.object({
            id: z.string(),
            name: z.string(),
            info: z.string().catch(''),
            params: z.record(z.union([z.string(),z.boolean()])),
        })
    )
});
export type FilterSubMenuValue = z.infer<typeof FilterSubMenuValueSchema>;


const AnalyticsCfgSchemaWithoutRelated = z.object({
    fld: z.record( AnalyticsCfgFldTypeSchema ),
    fld_md5: z.string().optional(),
    fld_order: z.array(z.string()),
    fld_period_range: z.object({ min: z.string(), max: z.string() }).optional(),
    fld_period: z.string().optional(),
    dbname: z.string(),
    data_category: z.string(),
    objicon: z.string().optional(),
    group: FilterGroupsSchema,
    column:  FilterColumnsSchema,
    object_zoom_threshold: z.number().optional(),
    clusters: z.record(AnalyticsCfgClusterSubMenuSchema).or(z.array(z.any()).length(0)).optional(),

    // added 2023-07-19
    related_to: z.coerce.number().optional(),
    collection_id: z.number().optional(),
    collection_name: z.string().optional(),
    fld_hidden: z.record(z.literal(1)).optional(),
    template: z.object({
        long: z.string(),
        short: z.string()
    }).optional(),
    influence_zone_items: z.record(
        z.string(),
        z.object({
            name: z.string(),
            type: z.string().describe('time|distance|radius|polygon'),
            subtype: z.string().describe('walk|bike|car'),
            value: z.coerce.number(),
            unit: z.string(),
            influence_zone_id: z.string().optional().describe('needed to relate with column of collection, look for influence_zone_id in $_CFG.fld items'),
            default : z.boolean().default(false),
        })
    ).optional()
});

export const AnalyticsCfgSchema = AnalyticsCfgSchemaWithoutRelated.merge(
    // z.object({ related: z.union([z.record(AnalyticsCfgSchemaWithoutRelated), z.tuple([])])})
    z.object({
        related: z.record(AnalyticsCfgSchemaWithoutRelated).or(z.array(z.any()).length(0))
    })
);


export interface AnalyticsCfg extends z.infer<typeof AnalyticsCfgSchema> { }


export const AnalyticsCfgFilterMarketsFieldSchema = z.object({
    t: z.string(),
    v: z.union([z.array(z.union([z.string(), z.number()])), z.string()]).optional(),
    c: z.string().optional(),
    max: z.number().optional(),
    min: z.number().optional(),
    negation: z.union([z.boolean(),z.null()]).optional(),
    l: z.array(z.string()).optional(),
});

export interface AnalyticsCfgFilterMarketsField extends z.infer<typeof AnalyticsCfgFilterMarketsFieldSchema> { }

export interface AnalyticsCfgFilter {
    [key: string]: { [key: string]: AnalyticsCfgFilterMarketsField }[] | [];
}


export interface RelationType {
    id: string;
    type: string; // "period" | "value";
    c: AnalyticsCfgFilterComparition | FilterRelatedPeriodComparition;
    unit: string;
    v: (string | number)[];
    db_name: string;
    base_period: string;
    second_period: string;
    comparing_field: string;
    a: string; // max
}
export type RelationsType = RelationType[];



export const TheViewColorizeSchema = z.object({
    v: z.union([z.array(z.string()), z.array(z.number())]),
    c: z.array(z.string().length(6)),
    s: z.boolean(),
    crit: z.object({
        f: z.string().describe('ex. c_trx_contribution'),
        p: z.union([z.literal(false), z.string().regex(/^\d{4}-\d{2}$/)]).describe('ex. 2021-01'),
        pref: z.union([z.literal(false), z.string().regex(/^\d{4}-\d{2}$/)]).catch(false).describe('for related colorize'),
        r: z.string().nullable().catch(null).describe('ex. finance'),
    }).optional().describe('for related colorize'),
    filter: z.union([z.boolean(), z.literal(0)]).catch(false),
    criteria_type: z.enum(["dictionary", "range"]).optional().catch('range'),
    allDictElements: z.number().optional(),
    defaultObjectColor: z.string().optional(),
    names: z.array(z.string()).optional(),
    v_min: z.number().optional().catch(0),
    v_max: z.number().optional().catch(0),
    qc: z.number().optional(),
    f: z.string().optional(),
    f_out: z.string().optional().describe('maybe for related colorize, ex: c_trx_contribution_0'),
    lastClicked: z.string().optional(),
    // related_options: z.object({
    //     date_first: z.string().nullable(),
    //     date_second: z.string().nullable(),
    //     compare_dates: z.boolean().nullable(),
    // }),
});
export interface TheViewColorizeType extends z.infer<typeof TheViewColorizeSchema> { }

export const ClusterCfgSchema = z.record(z.union([z.string(),z.boolean()]));
export type ClusterCfgType = z.infer<typeof ClusterCfgSchema>;
